import React, { useState, useMemo, useCallback } from 'react';
import './ExerciseSelectionModal.css';
import {
  HeartIcon,
  FireIcon,
  SunIcon,
  TrophyIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import ExerciseDetailModal from './ExerciseDetailModal';

const iconMap = {
  cardio: <HeartIcon className="category-icon" />,
  strength: <FireIcon className="category-icon" />,
  stretching: <SunIcon className="category-icon" />,
  sport: <TrophyIcon className="category-icon" />,
  static: <ClockIcon className="category-icon" />,
};

const ExerciseSelectionModal = ({ exercises, close, onSelect = () => {}, backendService }) => {
  const [selectedExerciseId, setSelectedExerciseId] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [detailExerciseId, setDetailExerciseId] = useState(null);

  // Define fixed categories
  const fixedCategories = useMemo(() => ['strength', 'static', 'stretching', 'cardio', 'sport'], []);
  const categories = ['all', ...fixedCategories];

  // Normalize exercise categories using useMemo for performance optimization
  const normalizedExercises = useMemo(() =>
    exercises.map(exercise => ({
      ...exercise,
      category: fixedCategories.includes(exercise.category?.toLowerCase()) ? exercise.category.toLowerCase() : 'strength'
    })),
    [exercises, fixedCategories]
  );

  // Filter and sort exercises
  const filteredExercises = useMemo(() => {
    return normalizedExercises
      .filter(exercise =>
        (selectedCategory === 'all' || exercise.category === selectedCategory) &&
        exercise.name.toLowerCase().includes(filterText.toLowerCase())
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [normalizedExercises, selectedCategory, filterText]);

  const handleFilterChange = useCallback((event) => {
    setFilterText(event.target.value);
  }, []);

  const handleCategoryChange = useCallback((category) => {
    setSelectedCategory(category);
  }, []);

  const handleSelect = useCallback(() => {
    if (selectedExerciseId) {
      onSelect(selectedExerciseId);
      close();
    }
  }, [selectedExerciseId, onSelect, close]);

  const handleExerciseClick = useCallback((exerciseId) => {
    setSelectedExerciseId(exerciseId);
  }, []);

  const openDetail = useCallback((e, exerciseId) => {
    e.stopPropagation();
    setDetailExerciseId(exerciseId);
  }, []);

  const closeDetail = useCallback(() => {
    setDetailExerciseId(null);
  }, []);

  return (
    <div className="exercise-selection-modal">
      <div className="category-selector">
        {categories.map(category => {
          const isActive = selectedCategory === category;
          return (
            <button
              key={category}
              onClick={() => handleCategoryChange(category)}
              className={`category-button ${isActive ? 'active' : ''}`}
            >
              {category !== 'all' && iconMap[category]}
              <span className="category-name">
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </span>
            </button>
          );
        })}
      </div>

      <div className="search-container">
        <div className="search-input-wrapper">
          <MagnifyingGlassIcon className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Search exercises..."
            value={filterText}
            onChange={handleFilterChange}
            autoFocus
          />
        </div>
      </div>

      <div className="exercise-list-container">
        <div className="exercise-list">
          {filteredExercises.length > 0 ? (
            filteredExercises.map(exercise => (
              <div
                key={exercise._id}
                className={`exercise-item ${selectedExerciseId === exercise._id ? 'selected' : ''}`}
                onClick={() => handleExerciseClick(exercise._id)}
              >
                <div className="exercise-row">
                  <span className="exercise-icon-wrapper">
                    {iconMap[exercise.category] || iconMap.strength}
                  </span>
                  <span className="exercise-name">{exercise.name}</span>
                  <button 
                    className="info-button"
                    onClick={(e) => openDetail(e, exercise._id)}
                    title="View Details"
                  >
                    <InformationCircleIcon className="info-icon" />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="no-results">No exercises found</div>
          )}
        </div>
      </div>

      <div className="button-container">
        <button 
          className="btn-add" 
          onClick={handleSelect} 
          disabled={!selectedExerciseId}
        >
          Add Exercise
        </button>
        <button className="btn-cancel" onClick={close}>Cancel</button>
      </div>

      {detailExerciseId && (
        <div className="detail-modal-overlay">
           <div className="detail-modal-content" onClick={e => e.stopPropagation()}>
             <button className="detail-close-button" onClick={closeDetail}>
               <XMarkIcon className="detail-close-icon" />
             </button>
             <ExerciseDetailModal 
               isOpen={true} 
               exerciseId={detailExerciseId} 
               close={closeDetail} 
               backendService={backendService}
             />
           </div>
        </div>
      )}
    </div>
  );
};

export default ExerciseSelectionModal;